import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import DonationQuickAction from '../../components/ui/DonationQuickAction';
import WelcomeHero from './components/WelcomeHero';
import QuickStats from './components/QuickStats';
import ActivityCard from './components/ActivityCard';
import CauseRecommendationCard from './components/CauseRecommendationCard';
import FeaturedChallenge from './components/FeaturedChallenge';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ProfileSection from './components/ProfileSection';

const Homepage = () => {
  const [activities, setActivities] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [featuredChallenge, setFeaturedChallenge] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const navigate = useNavigate();
  const { userProfile, loading: authLoading } = useAuth();

  // Create user object from authenticated profile
  const currentUser = userProfile ? {
    name: userProfile?.full_name || 'User',
    email: userProfile?.email || '',
    totalDonated: "0", // Will be fetched from donations table in future
    causesSupported: 0, // Will be calculated from donations in future
    impactLevel: "Bronze" // Will be calculated based on donations in future
  } : null;

  // Mock community stats
  const communityStats = {
    totalDonated: 1250000,
    activeDonors: 15420,
    causesSupported: 340,
    impactScore: 98
  };

  // Mock activity data
  const mockActivities = [
    {
      id: 1,
      donor: { name: "Sarah Johnson", avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face" },
      message: "Just donated to help provide clean water to rural communities in Kenya. Every drop counts! 💧",
      charity: {
        name: "Water for Life Foundation",
        category: "Water & Sanitation",
        image: "https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?w=200&h=200&fit=crop"
      },
      amount: 75,
      impact: "Your donation will provide clean water for 3 families for a month",
      timestamp: new Date(Date.now() - 1800000),
      likes: 24,
      comments: 8,
      isLiked: false,
      recentComments: [
        { author: "Mike Chen", text: "Amazing work! 🙌" },
        { author: "Lisa Park", text: "Inspired to donate too!" }
      ]
    },
    {
      id: 2,
      donor: { name: "Michael Rodriguez", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face" },
      message: "Supporting education for underprivileged children. Knowledge is power! 📚",
      charity: {
        name: "Future Leaders Academy",
        category: "Education",
        image: "https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=200&h=200&fit=crop"
      },
      amount: 150,
      impact: "Your contribution will sponsor school supplies for 5 students",
      timestamp: new Date(Date.now() - 3600000),
      likes: 31,
      comments: 12,
      isLiked: true,
      recentComments: [
        { author: "Emma Wilson", text: "Education changes everything!" },
        { author: "David Kim", text: "Thank you for making a difference" }
      ]
    },
    {
      id: 3,
      donor: { name: "Emily Chen", avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face" },
      message: "Helped fund medical supplies for disaster relief efforts. Together we can rebuild! 🏥",
      charity: {
        name: "Global Emergency Response",
        category: "Disaster Relief",
        image: "https://images.unsplash.com/photo-1584515933487-779824d29309?w=200&h=200&fit=crop"
      },
      amount: 200,
      impact: "Your donation will provide medical aid for 10 families",
      timestamp: new Date(Date.now() - 7200000),
      likes: 45,
      comments: 15,
      isLiked: false,
      recentComments: [
        { author: "John Smith", text: "Critical support when it\'s needed most" },
        { author: "Maria Garcia", text: "Heroes like you make recovery possible" }
      ]
    }
  ];

  // Mock cause recommendations
  const mockRecommendations = [
    {
      id: 1,
      name: "Save the Amazon Rainforest",
      category: "Environment",
      description: "Help protect one of Earth\'s most vital ecosystems by supporting reforestation efforts and indigenous communities in the Amazon basin.",
      image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop",
      raised: 125000,
      goal: 200000,
      donors: 1240,
      urgency: "high",
      isVerified: true,
      impact: "$50 plants 25 trees and protects 1 acre of rainforest"
    },
    {
      id: 2,
      name: "Mobile Health Clinics for Rural Areas",
      category: "Healthcare",
      description: "Bring essential medical care to remote communities through mobile health units equipped with modern medical equipment and trained staff.",
      image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop",
      raised: 85000,
      goal: 150000,
      donors: 890,
      urgency: "medium",
      isVerified: true,
      impact: "$100 provides medical care for 20 patients in remote areas"
    },
    {
      id: 3,
      name: "Digital Learning Centers",
      category: "Education",
      description: "Establish computer labs and internet access in underserved schools to bridge the digital divide and prepare students for the future.",
      image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=300&fit=crop",
      raised: 45000,
      goal: 100000,
      donors: 520,
      urgency: "low",
      isVerified: true,
      impact: "$75 provides digital literacy training for 5 students"
    }
  ];

  // Mock featured challenge
  const mockChallenge = {
    id: 1,
    title: "Holiday Giving Challenge",
    description: "Join thousands of donors in our year-end giving challenge. Let's make this holiday season extra special for those in need!",
    currentParticipants: 2847,
    targetParticipants: 5000,
    totalRaised: 485000,
    causesSupported: 45,
    endDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
    recentParticipants: [
      { name: "Alex Thompson" },
      { name: "Rachel Green" },
      { name: "James Wilson" },
      { name: "Sophie Brown" },
      { name: "Daniel Lee" }
    ]
  };

  const activityFilters = [
    { key: 'all', label: 'All Activity', icon: 'Activity' },
    { key: 'donations', label: 'Donations', icon: 'DollarSign' },
    { key: 'challenges', label: 'Challenges', icon: 'Trophy' },
    { key: 'following', label: 'Following', icon: 'Users' }
  ];

  useEffect(() => {
    // Simulate loading data
    const loadData = async () => {
      // Wait for auth to finish loading
      if (authLoading) return;
      
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setActivities(mockActivities);
      setRecommendations(mockRecommendations);
      setFeaturedChallenge(mockChallenge);
      setIsLoading(false);
    };

    loadData();
  }, [authLoading]);

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      navigate(`/cause-discovery?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const filteredActivities = activities?.filter(activity => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'donations') return activity?.amount > 0;
    if (activeFilter === 'following') return activity?.donor?.name !== currentUser?.name;
    return true;
  });

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        {/* Welcome Section */}
        {currentUser && <WelcomeHero user={currentUser} />}

        {/* Profile Section - NEW */}
        {currentUser && <ProfileSection />}

        {/* Community Stats */}
        <QuickStats stats={communityStats} />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Activity Feed */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">Community Activity</h2>
              <Button
                variant="outline"
                onClick={() => navigate('/community-feed')}
                iconName="ArrowRight"
                iconPosition="right"
              >
                View All
              </Button>
            </div>

            {/* Activity Filters */}
            <div className="flex items-center space-x-2 mb-6 overflow-x-auto">
              {activityFilters?.map((filter) => (
                <Button
                  key={filter?.key}
                  variant={activeFilter === filter?.key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveFilter(filter?.key)}
                  iconName={filter?.icon}
                  iconPosition="left"
                  className="whitespace-nowrap"
                >
                  {filter?.label}
                </Button>
              ))}
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mb-6">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search activities, causes, or donors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pr-12"
                />
                <Button
                  type="submit"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2"
                >
                  <Icon name="Search" size={16} />
                </Button>
              </div>
            </form>

            {/* Activity Cards */}
            <div className="space-y-6">
              {filteredActivities?.map((activity) => (
                <ActivityCard key={activity?.id} activity={activity} />
              ))}
            </div>

            {/* Load More */}
            <div className="text-center mt-8">
              <Button variant="outline" iconName="RefreshCw" iconPosition="left">
                Load More Activities
              </Button>
            </div>

            {/* Featured Challenge */}
            {featuredChallenge && (
              <div className="mt-8">
                <FeaturedChallenge challenge={featuredChallenge} />
              </div>
            )}
          </div>

          {/* Recommendations Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Recommended for You</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/cause-discovery')}
                  >
                    <Icon name="RefreshCw" size={16} />
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {recommendations?.map((cause) => (
                    <CauseRecommendationCard key={cause?.id} cause={cause} />
                  ))}
                </div>

                <div className="mt-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/cause-discovery')}
                    className="w-full"
                    iconName="Search"
                    iconPosition="left"
                  >
                    Discover More Causes
                  </Button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h4 className="font-semibold text-card-foreground mb-3">Quick Actions</h4>
                <div className="space-y-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/giving-challenges')}
                    className="w-full justify-start"
                    iconName="Trophy"
                    iconPosition="left"
                  >
                    Browse Challenges
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/user-dashboard')}
                    className="w-full justify-start"
                    iconName="BarChart3"
                    iconPosition="left"
                  >
                    View My Impact
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/community-feed')}
                    className="w-full justify-start"
                    iconName="Users"
                    iconPosition="left"
                  >
                    Connect with Donors
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <DonationQuickAction />
    </div>
  );
};

export default Homepage;